import type { TileRef } from "../document/types.ts";
import { DEFAULT_VARIANT, frameId } from "./manifest.ts";

/**
 * The asset's id (ADR-130 decision 1.1). A prop asset is named by the frame id of its first frame, a tileset
 * asset by the tileset id and a terrain asset by the terrain id, all of which a saved world already holds, so a
 * `{ tileset, tile }` reference maps to its asset by one pure function and no table.
 */

const SEPARATOR = "/";
const WHOLE_TILE = /^(0|[1-9][0-9]*)$/;

/**
 * What an id names: a frame (`<tileset>/<variant>/<tile>`), a terrain (`<tileset>/<set>`) or a whole tileset.
 */
export type AssetKind = "frame" | "terrain" | "tileset";

/**
 * The asset id of a tile reference: its frame id in the default variant. A mirror flag is a property of one
 * placement, not of the asset, so a flipped reference names the same asset.
 *
 * @param ref The reference.
 * @returns `<tileset>/default/<tile>`.
 * @throws Error when the tileset id is empty or holds a slash, or the tile is not a non-negative integer.
 */
export function assetIdOf(ref: TileRef): string {
  return frameId(ref.tileset, null, ref.tile);
}

/**
 * The tile reference an asset id names: the inverse of `assetIdOf`.
 *
 * @param id An asset id.
 * @returns The reference, or null when the id is not a frame id of the default variant (a tileset, a terrain, a
 *   frame of another variant, or no id at all).
 */
export function tileRefOfAsset(id: string): Pick<TileRef, "tileset" | "tile"> | null {
  const parts = id.split(SEPARATOR);
  if (parts.length !== 3 || assetKindOf(id) !== "frame" || parts[1] !== DEFAULT_VARIANT) {
    return null;
  }
  return { tileset: parts[0] as string, tile: Number(parts[2]) };
}

/**
 * Tells what an id names by its shape: no slash is a tileset, three parts ending in a whole tile number a frame,
 * and any other id with a slash a terrain, since a wang set's name may hold slashes of its own
 * (`old-prison:Tileset-Terrain-old prison/grass/dirt`). A terrain whose set is named `<word>/<whole number>` would
 * read as a frame; no pack holds one, and a caller that knows which it asked for does not need the shape.
 *
 * @param id An id.
 * @returns The kind, or null for an empty id or one with an empty part.
 */
export function assetKindOf(id: string): AssetKind | null {
  const parts = id.split(SEPARATOR);
  if (parts.some((part) => part === "")) {
    return null;
  }
  if (parts.length === 1) {
    return "tileset";
  }
  return parts.length === 3 && WHOLE_TILE.test(parts[2] as string) ? "frame" : "terrain";
}
